import React from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import _FA from 'admin/components/atoms/FontAwesome';
import { isAllowedPath } from 'admin/routes';
import NavGroup from './NavGroup';
import styles from './styles.scss';
import moment from 'moment';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import {
  selectUserPrivateMoney,
  selectShowMerchantApplication,
} from '~/javascripts/admin/reducers/user-private-money';
import BuildingIcon from '~/javascripts/admin/icons/building.svg';
import ShopIcon from '~/javascripts/admin/icons/shop.svg';

const FA = styled(_FA)`
  width: 22px;
`;

const AllowedNavLink = ({ to, user, children, ...rest }) => {
  const selectedPrivateMoney = useAppSelector(selectUserPrivateMoney);
  const path = to.split('?')[0];
  return isAllowedPath(path, { user, selectedPrivateMoney }) ?
         (<NavLink to={to} {...rest}>{children}</NavLink>) :
         null;
};

const NavGroupOrNull = ({ children, ...rest }) => {
  const selectedPrivateMoney = useAppSelector(selectUserPrivateMoney);

  if (!(children instanceof Array)) {
    children = [children];
  }

  // 表示モード等で false / null を返した条件付きレンダリングは除外する
  const validChildren = children.filter(React.isValidElement);

  if (validChildren.length === 0) {
    return null;
  }

  return validChildren.every(c => {
    const path = c.props.children.props.to.split('?')[0];
    const user = c.props.children.props.user;

    return !isAllowedPath(path, { user, selectedPrivateMoney });
  }) ? null : <NavGroup {...rest}>{validChildren}</NavGroup>;
};

export default ({ user }) => {
  // 加盟店 申込→審査→承認 フローの表示可否。選択中マネーの発行体設定に追従する。
  const showMerchantApplication = useAppSelector(selectShowMerchantApplication);
  const activeStyle = { background: 'rgb(0,162,63)' };
  const activeStyleInGroup = { color: '#bbb' };
  const isSettingSelected = window.location.pathname.includes('/settings');
  const settingLabelName = (<span><FA icon="cog" />各種設定</span>);
  const isServiceSettingSelected = window.location.pathname.includes('/service-settings');
  const serviceSettingLabelName = (<span><FA icon="cog" />サービス管理</span>);
  const isPromotionSelected = window.location.pathname.includes('/promotion');
  const promotionLabelName = (<span><FA icon="ticket-alt" />販促管理</span>);
  const isClearingSelected = window.location.pathname.includes('/clearing');
  const clearingLabelName = (<span><FA icon="money-bill-alt" />精算管理</span>);
  const clearingsPath = `/clearings?closing_date_from=${moment().subtract(7, 'days').startOf('day').toISOString()}&closing_date_to=${moment().endOf('day').toISOString()}`
  const isTransactionsSelected = window.location.pathname.includes('/transactions');
  const transactionsLabelName = (<span><FA icon="exchange-alt" />取引管理</span>);
  const transactionsPath = `/transactions?from=${moment().subtract(1, 'month').add(1, 'day').startOf('day').toISOString()}&to=${moment().endOf('day').toISOString()}`
  const transactionsExpiredPath = `/transactions/expired?from=${moment().subtract(1, 'months').startOf('month').toISOString()}&to=${moment().subtract(1, 'months').endOf('month').toISOString()}`
  const transactionsC2CPath = `/transactions/transfer?from=${moment().subtract(1, 'months').startOf('day').toISOString()}&to=${moment().endOf('day').toISOString()}`;
  const isCvsHistorySelected = window.location.pathname.includes('/cvs-request');
  const cvsPath = `/cvs-request?pay_limit_from=${moment().subtract(1, 'month').startOf('month').toISOString()}&pay_limit_to=${moment().add(1, 'month').endOf('month').toISOString()}`
  const myNumberAuthPath = `/itrust-request?from=${moment().subtract(1, 'month').startOf('month').toISOString()}&to=${moment().subtract(1, 'month').endOf('month').toISOString()}`
  const isMyNumberAuthSelected = window.location.pathname.includes('/itrust-request');
  const organizationLabelName = (<span style={{ display: "inline-flex"}}><img src={BuildingIcon} alt="organization icon" style={{ marginRight: "8px" }} />決済加盟店管理</span>)
  const isOrganizationSelected = window.location.pathname.includes('/organizations');
  const merchantShopLabelName = (<span style={{ display: "inline-flex"}}><img src={ShopIcon} alt="organization icon" style={{ marginRight: "8px" }} />店舗管理</span>)
  const isShopSelected = window.location.pathname === '/shops' || window.location.pathname.startsWith('/shops/');
  const shopLabelName = (<span><FA icon="store" />店舗管理</span>);

  return (
    <div className={styles.pb_sidebar}>
      <nav>
        <AllowedNavLink user={user} exact to="/" className="nav-link" activeStyle={activeStyle}><FA icon="home" />ホーム</AllowedNavLink>
        <NavGroupOrNull isGroupSelected={isTransactionsSelected || isCvsHistorySelected || isMyNumberAuthSelected} name={transactionsLabelName} id="transactions_group">
          <li>
          <AllowedNavLink
            exact
            user={user}
            to={transactionsPath}
            className="nav-link"
            activeStyle={activeStyleInGroup}
            isActive={(_, location) => {
              // Match '/transactions' and any sub-route like '/transactions/:id' except /transactions/expired which technically fulfills /transactions/:id
              return location.pathname.startsWith('/transactions') && !location.pathname.includes('/expired') && !location.pathname.includes('/transfer');
            }}
          >
            取引一覧
          </AllowedNavLink>
          </li>
          <li>
          <AllowedNavLink
            user={user}
            to={transactionsExpiredPath}
            className="nav-link"
            activeStyle={activeStyleInGroup}
            isActive={(_, location) => {
              // Match '/transactions/expired' only
              return location.pathname === '/transactions/expired';
            }}
          >
            失効履歴
          </AllowedNavLink>
          </li>
          <li>
          <AllowedNavLink
            user={user}
            to={transactionsC2CPath}
            className="nav-link"
            activeStyle={activeStyleInGroup}
            isActive={(_, location) => {
              // Match '/transactions/expired' only
              return location.pathname === '/transactions/transfer';
            }}
          >
            マネー譲渡履歴
          </AllowedNavLink>
          </li>
          <li>
            <AllowedNavLink user={user} exact to={cvsPath} className="nav-link" activeStyle={activeStyleInGroup}>コンビニ決済申込履歴</AllowedNavLink>
          </li>
          <li>
            <AllowedNavLink user={user} exact to={myNumberAuthPath} className="nav-link" activeStyle={activeStyleInGroup}>マイナンバー認証履歴</AllowedNavLink>
          </li>
        </NavGroupOrNull>
        <AllowedNavLink user={user} exact to="/customers" className="nav-link" activeStyle={activeStyle}><FA icon="users" />お客様管理</AllowedNavLink>
        <NavGroupOrNull isGroupSelected={isClearingSelected} name={clearingLabelName} id="clearing_group">
          <li><AllowedNavLink user={user} exact to="/clearing/summary" className="nav-link" activeStyle={activeStyleInGroup}>精算サマリ</AllowedNavLink></li>
          <li><AllowedNavLink user={user} exact to="/clearing" className="nav-link" activeStyle={activeStyleInGroup}>精算データ作成履歴</AllowedNavLink></li>
          <li><AllowedNavLink user={user} to="/clearings/export-batches" className="nav-link" activeStyle={activeStyleInGroup} isActive={(_, location) => location.pathname.startsWith('/clearings/export-batches')}>振込データ履歴</AllowedNavLink></li>
          <li><AllowedNavLink user={user} exact to={clearingsPath} className="nav-link" activeStyle={activeStyleInGroup}>精算データ一覧</AllowedNavLink></li>
          <li><AllowedNavLink user={user} exact to="/clearing/account" className="nav-link" activeStyle={activeStyleInGroup}>振込元口座</AllowedNavLink></li>
        </NavGroupOrNull>
        <NavGroupOrNull isGroupSelected={isPromotionSelected} name={promotionLabelName} id="promotion_group">
          <li><AllowedNavLink user={user} to="/promotion/campaigns" className="nav-link" activeStyle={activeStyleInGroup}>キャンペーン管理</AllowedNavLink></li>
          <li><AllowedNavLink user={user} exact to="/promotion/coupons" className="nav-link" activeStyle={activeStyleInGroup}>クーポン管理</AllowedNavLink></li>
          <li><AllowedNavLink user={user} exact to="/promotion/rewards" className="nav-link" activeStyle={activeStyleInGroup}>マネー/ポイント付与管理</AllowedNavLink></li>
          <li><AllowedNavLink user={user} exact to="/promotion/mobile-push-operations" className="nav-link" activeStyle={activeStyleInGroup}>プッシュメッセージ</AllowedNavLink></li>
          <li><AllowedNavLink user={user} exact to="/promotion/shop-qr-design" className="nav-link" activeStyle={activeStyleInGroup}>店舗掲示用QR</AllowedNavLink></li>
          <li><AllowedNavLink user={user} exact to="/promotion/bills" className="nav-link" activeStyle={activeStyleInGroup}>支払いQRコード管理</AllowedNavLink></li>
          <li><AllowedNavLink user={user} exact to="/promotion/checks" className="nav-link" activeStyle={activeStyleInGroup}>チャージQRコード管理</AllowedNavLink></li>
        </NavGroupOrNull>
        <NavGroupOrNull isGroupSelected={isShopSelected} name={shopLabelName} id="shops_group">
          <li><AllowedNavLink user={user} exact to="/shops" className="nav-link" activeStyle={activeStyleInGroup}>店舗一覧</AllowedNavLink></li>
          <li><AllowedNavLink user={user} to="/shops/create" className="nav-link" activeStyle={activeStyleInGroup}>店舗登録</AllowedNavLink></li>
          {/* 申込フロー系メニュー: AllowedNavLink 内でも requireMerchantApplication で判定されるが、
              ここで外側を showMerchantApplication で囲むのは (1) OFF 時に空の <li> を描画しないため、
              (2) NavGroupOrNull が React.isValidElement で false を除外しグループ表示判定に含めないため。 */}
          {showMerchantApplication && <li><AllowedNavLink user={user} exact to="/shops/application-create" className="nav-link" activeStyle={activeStyleInGroup}>店舗追加申請</AllowedNavLink></li>}
          {showMerchantApplication && <li><AllowedNavLink user={user} exact to="/shops/applications" className="nav-link" activeStyle={activeStyleInGroup}>加盟店舗申請管理</AllowedNavLink></li>}
        </NavGroupOrNull>
        <NavGroupOrNull isGroupSelected={isOrganizationSelected} name={organizationLabelName} id="organizations_group">
          <li><AllowedNavLink user={user} exact to="/organizations" className="nav-link" activeStyle={activeStyleInGroup}>決済加盟店一覧</AllowedNavLink></li>
          <li><AllowedNavLink user={user} exact to="/organizations/create" className="nav-link" activeStyle={activeStyleInGroup}>決済加盟店登録</AllowedNavLink></li>
          {showMerchantApplication && <li><AllowedNavLink user={user} exact to="/organizations/application" className="nav-link" activeStyle={activeStyleInGroup}>決済加盟店申請管理</AllowedNavLink></li>}
          {showMerchantApplication && <li><AllowedNavLink user={user} exact to="/organizations/application-form" className="nav-link" activeStyle={activeStyleInGroup}>加盟店申込フォーム</AllowedNavLink></li>}
        </NavGroupOrNull>
        <NavGroupOrNull isGroupSelected={isSettingSelected} name={settingLabelName} id="setting_group">
          <li><AllowedNavLink user={user} to="/settings/private-money" className="nav-link" activeStyle={activeStyleInGroup}>マネー設定</AllowedNavLink></li>
          <li><AllowedNavLink user={user} to="/settings/private-money-exchange" className="nav-link" activeStyle={activeStyleInGroup}>マネー間交換管理</AllowedNavLink></li>
          <li><AllowedNavLink user={user} to="/settings/self-organization-detail" className="nav-link" activeStyle={activeStyleInGroup}>組織情報</AllowedNavLink></li>
          <li><AllowedNavLink user={user} to="/settings/admin-users" className="nav-link" activeStyle={activeStyleInGroup}>管理ユーザ設定</AllowedNavLink></li>
          <li><AllowedNavLink user={user} exact to="/settings/devices" className="nav-link" activeStyle={activeStyleInGroup}>デバイス管理</AllowedNavLink></li>
          <li><AllowedNavLink user={user} to="/settings/devices/create" className="nav-link" activeStyle={activeStyleInGroup}>デバイス登録</AllowedNavLink></li>
          <li><AllowedNavLink user={user} to="/settings/shop-label" className="nav-link" activeStyle={activeStyleInGroup}>店舗ラベル管理</AllowedNavLink></li>
          <li><AllowedNavLink user={user} to="/settings/device-keys" className="nav-link" activeStyle={activeStyleInGroup}>社外デバイス管理</AllowedNavLink></li>
          <li><AllowedNavLink user={user} to="/settings/system" className="nav-link" activeStyle={activeStyleInGroup}>システム設定</AllowedNavLink></li>
          <li><AllowedNavLink user={user} to="/settings/qr-design" className="nav-link" activeStyle={activeStyleInGroup}>QRデザイン</AllowedNavLink></li>
        </NavGroupOrNull>
        <NavGroupOrNull isGroupSelected={isServiceSettingSelected} name={serviceSettingLabelName} id="service_setting_group">
          <li><AllowedNavLink user={user} to="/service-settings/all-mobile-push-operations" className="nav-link" activeStyle={activeStyleInGroup}>全組織プッシュメッセージ</AllowedNavLink></li>
          <li><AllowedNavLink user={user} to="/service-settings/customer" className="nav-link" activeStyle={activeStyleInGroup}>お客様設定</AllowedNavLink></li>
          <li><AllowedNavLink user={user} to="/service-settings/campaigns" className="nav-link" activeStyle={activeStyleInGroup}>キャンペーン登録</AllowedNavLink></li>
          <li><AllowedNavLink user={user} to="/service-settings/issuer-organization" className="nav-link" activeStyle={activeStyleInGroup}>発行体管理</AllowedNavLink></li>
          <li><AllowedNavLink user={user} to="/service-settings/private-money/rename" className="nav-link" activeStyle={activeStyleInGroup}>マネー変更</AllowedNavLink></li>
          <li><AllowedNavLink user={user} to="/service-settings/private-money/create" className="nav-link" activeStyle={activeStyleInGroup}>マネー登録</AllowedNavLink></li>
          <li><AllowedNavLink user={user} to="/service-settings/user-attribute-schema/create" className="nav-link" activeStyle={activeStyleInGroup}>ウォレット登録時アンケート</AllowedNavLink></li>
          <li><AllowedNavLink user={user} to="/service-settings/partner-clients" className="nav-link" activeStyle={activeStyleInGroup}>PartnerClient</AllowedNavLink></li>
          <li><AllowedNavLink user={user} to="/service-settings/oauth-clients" className="nav-link" activeStyle={activeStyleInGroup}>OAuthClient</AllowedNavLink></li>
          <li><AllowedNavLink user={user} to="/service-settings/access-token" className="nav-link" activeStyle={activeStyleInGroup}>アクセストークン管理</AllowedNavLink></li>
          <li><AllowedNavLink user={user} to="/service-settings/software" className="nav-link" activeStyle={activeStyleInGroup}>ソフトウェアビルド管理</AllowedNavLink></li>
          <li><AllowedNavLink user={user} to="/service-settings/software-release" className="nav-link" activeStyle={activeStyleInGroup}>ソフトウェアリリース管理</AllowedNavLink></li>
        </NavGroupOrNull>
      </nav>
    </div>
  );
};
